import type { JsonSchema, TransformResult, TransformOptions } from "./types";

/**
 * Step 3: Normalize scalar anyOf and transform additionalProperties
 *
 * This transformation handles multiple cases:
 * 1. Scalar anyOf (number|string) → type: ["number", "string"]
 * 2. Array-or-null anyOf → type: "array" (null dropped)
 * 3. Object-or-null anyOf → type: "object" (null dropped)
 * 4. Array items anyOf normalization
 * 5. additionalProperties → array with __key field
 *
 * Recurses into oneOf/anyOf/allOf branches.
 *
 * @param schema - Schema after mergeAllOf
 * @param options - Transform options
 * @returns Transformed schema and metadata
 */
export function normalizeScalarAnyOf(
  schema: JsonSchema,
  options?: TransformOptions
): TransformResult {
  const { debug = false } = options || {};

  if (debug) {
    console.log("🔧 [normalizeScalarAnyOf] Starting...");
  }

  const transformedKeys: string[] = [];
  const flags: Record<string, any> = {};

  // Mutates schema in-place
  normalizeScalarAnyOfRecursive(schema, schema, transformedKeys, flags, debug);

  if (debug) {
    console.log("✅ [normalizeScalarAnyOf] Complete", {
      transformedKeys,
      flags: Object.keys(flags),
    });
  }

  return {
    schema,
    metadata: {
      name: "normalizeScalarAnyOf",
      transformedKeys,
      flags,
      skipped: false,
    },
  };
}

/**
 * Recursive worker function - mutates schema in place
 */
function normalizeScalarAnyOfRecursive(
  schema: any,
  rootSchema: any,
  transformedKeys: string[],
  flags: Record<string, any>,
  debug: boolean,
  path: string = ""
): void {
  const root = rootSchema ?? schema;
  if (!schema || typeof schema !== "object") {
    return;
  }

  // Recurse into combinators first
  if (Array.isArray(schema.oneOf)) {
    schema.oneOf.forEach((branch: any, i: number) =>
      normalizeScalarAnyOfRecursive(
        branch,
        root,
        transformedKeys,
        flags,
        debug,
        `${path}.oneOf[${i}]`
      )
    );
  }
  if (Array.isArray(schema.anyOf)) {
    schema.anyOf.forEach((branch: any, i: number) =>
      normalizeScalarAnyOfRecursive(
        branch,
        root,
        transformedKeys,
        flags,
        debug,
        `${path}.anyOf[${i}]`
      )
    );
  }
  if (Array.isArray(schema.allOf)) {
    schema.allOf.forEach((branch: any, i: number) =>
      normalizeScalarAnyOfRecursive(
        branch,
        root,
        transformedKeys,
        flags,
        debug,
        `${path}.allOf[${i}]`
      )
    );
  }

  if (schema.properties && typeof schema.properties === "object") {
    Object.keys(schema.properties).forEach((key) => {
      const property = schema.properties[key];
      if (!property || typeof property !== "object") {
        return;
      }

      const propertyPath = path ? `${path}.${key}` : key;

      const availableTypes: string[] = [];
      const pushType = (t: unknown) => {
        if (!t) return;
        if (Array.isArray(t)) {
          availableTypes.push(
            ...(t.filter((x) => typeof x === "string") as string[])
          );
        } else if (typeof t === "string") {
          availableTypes.push(t);
        }
      };

      if (property.type) {
        pushType(property.type);
      }

      // Process anyOf
      if (Array.isArray(property.anyOf)) {
        const anyOfArray = property.anyOf as any[];

        // Case 1: array-or-null anyOf
        const arrayBranch = anyOfArray.find(
          (item) =>
            item &&
            typeof item === "object" &&
            item.type === "array" &&
            item.items
        ) as any | undefined;
        const nullBranch = anyOfArray.find(
          (item) =>
            item &&
            typeof item === "object" &&
            (item.type === "null" ||
              (Array.isArray(item.type) && item.type.includes("null")))
        ) as any | undefined;

        if (arrayBranch && nullBranch && anyOfArray.length === 2) {
          property.type = "array";

          // Lift array constraints
          Object.keys(arrayBranch).forEach((k) => {
            if (k === "type") return;
            if (property[k] === undefined) {
              property[k] = arrayBranch[k];
            }
          });

          delete property.anyOf;

          if (debug) {
            console.log(
              `  ✓ Normalized array-or-null anyOf at ${propertyPath}`
            );
          }
          transformedKeys.push(propertyPath);

          // Recurse into items
          if (property.items && typeof property.items === "object") {
            normalizeScalarAnyOfRecursive(
              property.items,
              root,
              transformedKeys,
              flags,
              debug,
              `${propertyPath}.items`
            );
          }

          // Continue normal recursion
          normalizeScalarAnyOfRecursive(
            property,
            root,
            transformedKeys,
            flags,
            debug,
            propertyPath
          );
          return;
        }

        // Case 2: object-or-null anyOf
        const objectBranch = anyOfArray.find(
          (item) =>
            item &&
            typeof item === "object" &&
            (item.type === "object" || item.properties)
        ) as any | undefined;

        if (objectBranch && nullBranch && anyOfArray.length === 2) {
          property.type = "object";

          // Lift object constraints
          Object.keys(objectBranch).forEach((k) => {
            if (k === "type") return;
            if (property[k] === undefined) {
              property[k] = objectBranch[k];
            }
          });

          delete property.anyOf;

          if (debug) {
            console.log(
              `  ✓ Normalized object-or-null anyOf at ${propertyPath}`
            );
          }
          transformedKeys.push(propertyPath);

          // Continue normal recursion
          normalizeScalarAnyOfRecursive(
            property,
            root,
            transformedKeys,
            flags,
            debug,
            propertyPath
          );
          return;
        }

        // Case 3: all scalar anyOf
        const allScalar = anyOfArray.every((item) => {
          if (!item || typeof item !== "object") return false;
          if (item.type === "object" || item.properties || item.items)
            return false;
          return true;
        });

        if (allScalar) {
          anyOfArray.forEach((item: any) => {
            if (!item || typeof item !== "object") return;
            Object.keys(item).forEach((k) => {
              if (k === "type") {
                pushType(item[k]);
              } else if (property[k] === undefined) {
                property[k] = item[k];
              }
            });
          });
          delete property.anyOf;

          if (debug) {
            console.log(`  ✓ Flattened scalar anyOf at ${propertyPath}`);
          }
          transformedKeys.push(propertyPath);
        } else {
          // Not scalar - recurse into branches
          anyOfArray.forEach((item: any) =>
            normalizeScalarAnyOfRecursive(
              item,
              root,
              transformedKeys,
              flags,
              debug,
              `${propertyPath}.anyOf`
            )
          );
        }
      }

      // Set deduplicated type array
      if (availableTypes.length > 0) {
        property.type = Array.from(new Set(availableTypes));
      }

      // Process array items
      if (property.items && typeof property.items === "object") {
        const items = property.items as any;

        if (Array.isArray(items.anyOf)) {
          // Add titles to items.anyOf options
          items.anyOf.forEach((option: any) => {
            if (!option || typeof option !== "object") return;
            if (!option.title && option.type) option.title = option.type;
          });

          // Flatten scalar items.anyOf
          const itemAnyOfArray = items.anyOf as any[];
          const itemAllScalar = itemAnyOfArray.every((opt) => {
            if (!opt || typeof opt !== "object") return false;
            if (opt.type === "object" || opt.properties || opt.items)
              return false;
            return true;
          });

          if (itemAllScalar) {
            let chosenType: string | undefined = undefined;
            itemAnyOfArray.forEach((opt: any) => {
              if (!opt || typeof opt !== "object") return;
              const t = opt.type;
              const types = Array.isArray(t) ? t : [t];
              const nonNull = types.find((tt) => tt && tt !== "null");
              if (!chosenType && typeof nonNull === "string") {
                chosenType = nonNull;
                Object.keys(opt).forEach((k) => {
                  if (k === "type") return;
                  if (items[k] === undefined) {
                    items[k] = opt[k];
                  }
                });
              }
            });
            if (chosenType) {
              items.type = chosenType;
              delete items.anyOf;

              if (debug) {
                console.log(
                  `  ✓ Flattened items anyOf at ${propertyPath}.items`
                );
              }
              transformedKeys.push(`${propertyPath}.items`);
            }
          }
        }

        if (Array.isArray(items.type)) {
          const itemTypes = items.type as unknown[];
          const allPrimitive = itemTypes.every(
            (t) =>
              typeof t === "string" &&
              (t === "string" ||
                t === "number" ||
                t === "integer" ||
                t === "boolean" ||
                t === "null")
          );
          if (allPrimitive) {
            const nonNull = itemTypes.find(
              (t) => typeof t === "string" && t !== "null"
            ) as string | undefined;
            if (nonNull) {
              items.type = nonNull;

              if (debug) {
                console.log(
                  `  ✓ Flattened items type union at ${propertyPath}.items`
                );
              }
              transformedKeys.push(`${propertyPath}.items`);
            }
          }
        }

        normalizeScalarAnyOfRecursive(
          items,
          root,
          transformedKeys,
          flags,
          debug,
          `${propertyPath}.items`
        );
      }

      // Transform additionalProperties to array
      const isObjectType =
        property.type === "object" ||
        (Array.isArray(property.type) && property.type.includes("object"));

      if (isObjectType && property.additionalProperties) {
        const additionalPropsSchema = property.additionalProperties;

        // Skip if additionalProperties is just `true`
        if (additionalPropsSchema === true) {
          return;
        }

        // Only transform if it's an object schema
        if (
          typeof additionalPropsSchema === "object" &&
          additionalPropsSchema !== null
        ) {
          property.type = "array";
          property.items = {
            type: "object",
            properties: {
              __key: {
                type: "string",
                title: "Key",
                description: "The key name for this entry",
              },
              ...(additionalPropsSchema.properties || {}),
            },
            required: [
              "__key",
              ...(Array.isArray(additionalPropsSchema.required)
                ? additionalPropsSchema.required
                : []),
            ],
            ...(additionalPropsSchema.title
              ? { title: additionalPropsSchema.title }
              : {}),
            ...(additionalPropsSchema.description
              ? { description: additionalPropsSchema.description }
              : {}),
          };

          // Mark for reverse transformation
          property.__fromAdditionalProperties = true;
          flags[propertyPath] = "__fromAdditionalProperties";

          delete property.additionalProperties;

          if (debug) {
            console.log(
              `  ✓ Transformed additionalProperties to array at ${propertyPath}`
            );
          }
          transformedKeys.push(propertyPath);
        }
      }

      // Recurse into property
      normalizeScalarAnyOfRecursive(
        property,
        root,
        transformedKeys,
        flags,
        debug,
        propertyPath
      );
    });
  }
}
